import React, { useState, useRef, useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Button, Result, Spin, message } from 'antd';
import { initiateStkPush, checkStkStatus } from '../api';

const MpesaPaymentModal = ({ open, onClose, farmId, subscriptionId, planId, defaultAmount, onSuccess }) => {
    const [form] = Form.useForm();
    const [stage, setStage] = useState('form'); // form | polling | success | failed | cancelled
    const [resultDesc, setResultDesc] = useState('');
    const pollRef = useRef(null);

    useEffect(() => () => clearInterval(pollRef.current), []);

    const startPolling = (id) => {
        let attempts = 0;
        pollRef.current = setInterval(async () => {
            attempts += 1;
            try {
                const txn = await checkStkStatus(id);

                if (txn.status === 'success') {
                    clearInterval(pollRef.current);
                    setResultDesc(txn.result_desc);
                    setStage('success');
                    onSuccess?.();
                } else if (txn.status === 'cancelled') {
                    clearInterval(pollRef.current);
                    setResultDesc(txn.result_desc);
                    setStage('cancelled');
                } else if (txn.status === 'failed') {
                    clearInterval(pollRef.current);
                    setResultDesc(txn.result_desc);
                    setStage('failed');
                }
                // still pending — keep polling
            } catch (err) {
                console.error('Error polling status:', err);
            }

            if (attempts > 20) {
                clearInterval(pollRef.current);
                setStage('failed');
            }
        }, 3000);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setStage('polling');
            const res = await initiateStkPush({ farmId, subscriptionId, planId, phone: values.phone, amount: values.amount });
            message.info(res.customerMessage);
            startPolling(res.checkoutRequestId);
        } catch (err) {
            if (err?.errorFields) return;
            message.error(err.response?.data?.message || 'Failed to initiate payment');
            setStage('form');
        }
    };

    const handleClose = () => {
        clearInterval(pollRef.current);
        setStage('form');
        setResultDesc('');
        form.resetFields();
        onClose();
    };

    return (
        <Modal title="Pay with M-Pesa" open={open} onCancel={handleClose} footer={null} destroyOnClose>
            {stage === 'form' && (
                <Form form={form} layout="vertical" initialValues={{ amount: defaultAmount }}>
                    <Form.Item
                        name="phone"
                        label="M-Pesa Phone Number"
                        rules={[{ required: true }, { pattern: /^(0|254|\+254)7\d{8}$/, message: 'Enter a valid Safaricom number' }]}
                    >
                        <Input placeholder="0712345678" />
                    </Form.Item>
                    <Form.Item name="amount" label="Amount (KES)" rules={[{ required: true }]}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Button type="primary" block onClick={handleSubmit}>
                        Send STK Push
                    </Button>
                </Form>
            )}

            {stage === 'polling' && (
                <Result
                    icon={<Spin size="large" />}
                    title="Waiting for payment confirmation"
                    subTitle="Enter your M-Pesa PIN on your phone to complete the payment"
                />
            )}

            {stage === 'success' && (
                <Result
                    status="success"
                    title="Payment Successful"
                    subTitle={resultDesc}
                    extra={
                        <Button type="primary" onClick={handleClose}>
                            Done
                        </Button>
                    }
                />
            )}

            {stage === 'cancelled' && (
                <Result
                    status="warning"
                    title="Payment Cancelled"
                    subTitle={resultDesc}
                    extra={<Button onClick={() => setStage('form')}>Try Again</Button>}
                />
            )}

            {stage === 'failed' && (
                <Result
                    status="error"
                    title="Payment Failed"
                    subTitle={resultDesc}
                    extra={<Button onClick={() => setStage('form')}>Try Again</Button>}
                />
            )}
        </Modal>
    );
};

export default MpesaPaymentModal;
